// Pure helpers for the admin area (Turkish UI). No React, no network.

export const SLA_WARN_HOURS = 20;
export const REQUIRED_PAGES = ["impressum", "datenschutz", "agb"];
export const PAGE_SLUGS = ["impressum", "datenschutz", "agb", "widerruf"];

const toMs = (iso) => {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
};

export function lastMessage(ticket) {
  const msgs = (ticket && ticket.messages) || [];
  return msgs.length ? msgs[msgs.length - 1] : null;
}

// Ticket waits for an expert: not closed and the last word is the customer's.
export function isAwaitingExpert(ticket) {
  if (!ticket || ticket.status === "closed") return false;
  const last = lastMessage(ticket);
  return !last || last.from === "customer";
}

// Hours since the customer's unanswered message (plain hours, no business days).
export function awaitingAgeHours(ticket, now = Date.now()) {
  if (!isAwaitingExpert(ticket)) return 0;
  const last = lastMessage(ticket);
  const t = toMs(last ? last.at : ticket.created_at);
  if (t === null) return 0;
  return Math.max(0, (now - t) / 3600000);
}

export function isSlaWarning(ticket, now = Date.now(), hours = SLA_WARN_HOURS) {
  return isAwaitingExpert(ticket) && awaitingAgeHours(ticket, now) > hours;
}

// ---- orders (Stripe) ------------------------------------------------------
export const ORDER_STATUS = {
  pending: "Bekliyor",
  paid: "Ödendi",
  expired: "Süresi doldu",
  refunded: "İade edildi",
  failed: "Başarısız",
};
export const ORDER_PRODUCT = { report: "Ayrıntılı rapor", consulting: "Danışmanlık" };

// Paid report order that still needs the owner to write the detailed report.
export const needsPreparation = (o) => !!o && o.status === "paid" && o.product === "report" && !o.fulfilled;
export const countToPrepare = (orders) => (orders || []).filter(needsPreparation).length;

export const formatCents = (cents) =>
  `${(Number.isFinite(Number(cents)) ? Number(cents) / 100 : 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

// The three conditions behind `payments_live`; the client only sees the result.
export const PAYMENTS_REQUIREMENTS = [
  "Stripe anahtarları sunucuda tanımlı",
  "Ortam anahtarı PAYMENTS_ENABLED açık",
  "Yönetim ayarı: ödemeler etkin (yukarıdaki anahtar)",
];

export function paymentsStatusView({ paymentsEnabled, paymentsLive }) {
  if (paymentsLive === true) return { live: true, text: "Ödemeler canlı: satın alma butonları gerçek ödeme açar." };
  const hint = paymentsEnabled === true
    ? "Anahtar açık ama ödemeler canlı değil; şu üç koşuldan biri eksik: "
    : "Ödemeler canlı değil (talep akışı sürüyor). Canlı olması için üç koşul gerekir: ";
  return { live: false, text: hint + PAYMENTS_REQUIREMENTS.map((r, i) => `${i + 1}) ${r}`).join("; ") + "." };
}

export function computeOverview({ tickets = [], quotes = [], settings = null, orders = [], now = Date.now() } = {}) {
  return {
    toPrepare: countToPrepare(orders),
    openTickets: tickets.filter((t) => t.status === "open").length,
    slaWarnings: tickets.filter((t) => isSlaWarning(t, now)).length,
    newQuotes: quotes.filter((q) => q.status === "new").length,
    freeSlotsLeft: settings && typeof settings.free_slots_left === "number" ? settings.free_slots_left : null,
  };
}

export function formatAge(hours) {
  if (hours < 1) return "<1 sa";
  if (hours < 48) return `${Math.floor(hours)} sa`;
  return `${Math.floor(hours / 24)} gün`;
}

// ---- site settings form -------------------------------------------------
export const SETTINGS_FIELDS = [
  { key: "report_price_eur", label: "Rapor fiyatı (EUR)", min: 0, max: 10000 },
  { key: "consulting_price_eur", label: "Danışmanlık fiyatı (EUR)", min: 0, max: 10000 },
  { key: "consulting_days", label: "Danışmanlık süresi (gün)", min: 1, max: 365 },
  { key: "max_tickets", label: "Maks. destek talebi", min: 1, max: 1000 },
  { key: "response_hours", label: "Yanıt süresi (saat)", min: 1, max: 720 },
  { key: "free_slots_total", label: "Ücretsiz danışmanlık kontenjanı", min: 0, max: 100000 },
];

export function validateSettings(form) {
  const errors = {};
  for (const f of SETTINGS_FIELDS) {
    const raw = form ? form[f.key] : undefined;
    const s = raw === null || raw === undefined ? "" : String(raw).trim();
    if (s === "" || !/^-?\d+$/.test(s)) {
      errors[f.key] = "Tam sayı girin";
      continue;
    }
    const n = Number(s);
    if (n < f.min || n > f.max) errors[f.key] = `${f.min} - ${f.max} arası olmalı`;
  }
  return errors;
}

export function settingsPayload(form) {
  const out = {};
  for (const f of SETTINGS_FIELDS) out[f.key] = Number(String(form[f.key]).trim());
  return out;
}

// ---- discount form -------------------------------------------------------
export function validateDiscount(form) {
  const errors = {};
  const code = String(form.code || "").trim().toUpperCase();
  if (!/^[A-Z0-9_-]{3,32}$/.test(code)) errors.code = "3-32 karakter: A-Z, 0-9, _ veya -";
  const v = Number(form.value);
  if (form.value === "" || Number.isNaN(v)) errors.value = "Değer girin";
  else if (form.kind === "percent" && (!Number.isInteger(v) || v < 1 || v > 100)) errors.value = "Yüzde 1-100 arası tam sayı";
  else if (form.kind === "amount" && (v <= 0 || v > 10000)) errors.value = "Tutar 0 - 10000 arası";
  if (!form.applies_to || form.applies_to.length < 1) errors.applies_to = "En az bir ürün seçin";
  if (form.max_uses !== "" && form.max_uses != null) {
    const m = Number(form.max_uses);
    if (!Number.isInteger(m) || m < 1) errors.max_uses = "En az 1 olmalı";
  }
  if (form.valid_from && form.valid_until && Date.parse(form.valid_until) <= Date.parse(form.valid_from)) {
    errors.valid_until = "Bitiş, başlangıçtan sonra olmalı";
  }
  return errors;
}

export function discountPayload(form) {
  const p = {
    code: String(form.code).trim().toUpperCase(),
    kind: form.kind,
    value: Number(form.value),
    applies_to: form.applies_to,
    active: !!form.active,
  };
  if (form.valid_from) p.valid_from = new Date(form.valid_from).toISOString();
  if (form.valid_until) p.valid_until = new Date(`${String(form.valid_until).slice(0, 10)}T23:59:59`).toISOString();
  if (form.max_uses !== "" && form.max_uses != null) p.max_uses = Number(form.max_uses);
  if (form.note && form.note.trim()) p.note = form.note.trim();
  return p;
}

// ---- testimonials ----------------------------------------------------------
export function validateTestimonial(form) {
  const errors = {};
  if (!String(form.name || "").trim()) errors.name = "Ad girin";
  else if (form.name.length > 100) errors.name = "En fazla 100 karakter";
  if (form.company && form.company.length > 150) errors.company = "En fazla 150 karakter";
  if (!String(form.quote || "").trim()) errors.quote = "Yorum metni girin";
  else if (form.quote.length > 500) errors.quote = "En fazla 500 karakter";
  return errors;
}

export function testimonialPayload(form) {
  return {
    name: form.name.trim(),
    company: (form.company || "").trim(),
    quote: form.quote.trim(),
    active: !!form.active,
    sort_order: Number(form.sort_order) || 0,
  };
}

// ---- pages ---------------------------------------------------------------
export function missingRequiredPages(pages) {
  const published = new Set((pages || []).filter((p) => p.published).map((p) => p.slug));
  return REQUIRED_PAGES.filter((s) => !published.has(s));
}

// ---- mail test: /admin/mail-test response -> UI message + env-var checklist
export function mailTestView(r) {
  const res = r || {};
  const checklist = [
    { name: "RESEND_API_KEY", ok: !!res.key_configured },
    { name: "MAIL_FROM", ok: !!res.mail_from },
    { name: "ADMIN_NOTIFY_EMAIL", ok: !!res.notify_configured },
  ];
  if (res.sent) {
    return { ok: true, message: "Test e-postası gönderildi. Gelen kutunuzu kontrol edin.", checklist, missing: [] };
  }
  return {
    ok: false,
    message: `Gönderilemedi${res.error ? `: ${res.error}` : ""}`,
    checklist,
    missing: checklist.filter((c) => !c.ok).map((c) => c.name),
  };
}

export function apiErrorText(err, fallback = "İşlem başarısız") {
  const d = err && err.response && err.response.data && err.response.data.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d.length) return d.map((x) => x.msg || String(x)).join("; ");
  return fallback;
}

// ---- Eksik ölçümler (bulk completion; spends real money) ----
export const BACKFILL_MAX_USD = 5;

export function formatUsd(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "$0,00";
  return `$${v.toFixed(2).replace(".", ",")}`;
}

// Cap sent to the server: the estimate rounded UP to the cent (never below it).
export function backfillCap(estimated) {
  return Math.ceil((Number(estimated) || 0) * 100 - 1e-9) / 100;
}

export function backfillButtonState(plan, status, busy = false) {
  const running = !!busy || (status && status.state === "running");
  const n = plan ? plan.links_to_complete : 0;
  if (running) return { disabled: true, label: "Çalışıyor..." };
  if (!plan || n === 0) return { disabled: true, label: "Tamamlanacak link yok" };
  if (backfillCap(plan.estimated_usd) > BACKFILL_MAX_USD) return { disabled: true, label: "Tahmin üst sınırı aşıyor" };
  return { disabled: false, label: `Tahmini ${formatUsd(plan.estimated_usd)} harcayarak tamamla` };
}

export function backfillProgressText(status) {
  if (!status) return "";
  if (status.state === "running") return `${status.processed}/${status.total} link işleniyor`;
  if (status.state === "done") return `Bitti: ${status.processed}/${status.total} link, tahmini harcama ${formatUsd(status.spent_estimate_usd)}`;
  if (status.state === "error") return "Çalışma hatayla durdu";
  return "";
}
